import React, { useEffect, useState } from 'react';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { interactionEntity } from '../../config/entities/interactions';
import { crmService } from '../../services/crmCRUD';
import { useLanguage } from '../../state/language/LanguageProvider';
import { ViewModal } from '../../components/common/ViewModal';
import { EditModal } from '../../components/common/EditModal';

export const InteractionsPage: React.FC = () => {
  const { t } = useLanguage();
  
  // Modal state management
  const [selectedInteraction, setSelectedInteraction] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  // Total count state
  const [totalInteractions, setTotalInteractions] = useState<number>(0);
  const [createdInteraction, setCreatedInteraction] = useState<any>(null);

  // Dynamic options for selects
  const [contactOptions, setContactOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [companyOptions, setCompanyOptions] = useState<Array<{ value: string; label: string }>>([]);

  // Use Dedicated CRM Service (same as dashboard) with wrapper for FlexibleCRUDTable compatibility
  const interactionService = {
    getAll: (params: any) => crmService.getInteractions(params),
    get: (id: string) => crmService.getInteractionById(id),
    create: (data: any) => crmService.createInteraction(data),
    update: (id: string, data: any) => crmService.updateInteraction(id, data),
    delete: (id: string) => crmService.deleteInteraction(id)
  };
  
  const contactsService = {
    getAll: (params: any) => crmService.getContacts(params),
    get: (id: string) => crmService.getContactById(id),
    create: (data: any) => crmService.createContact(data),
    update: (id: string, data: any) => crmService.updateContact(id, data),
    delete: (id: string) => crmService.deleteContact(id)
  };
  
  const companiesService = {
    getAll: (params: any) => crmService.getCompanies(params),
    get: (id: string) => crmService.getCompanyById(id),
    create: (data: any) => crmService.createCompany(data),
    update: (id: string, data: any) => crmService.updateCompany(id, data),
    delete: (id: string) => crmService.deleteCompany(id)
  };

  // Load total interactions count
  useEffect(() => {
    const loadTotalCount = async () => {
      try {
        // Use a very high limit to ensure we get all records and the correct total
        const response = await crmService.getInteractions({ limit: 99999 });
        if (response && response.pagination && response.pagination.total) {
          setTotalInteractions(response.pagination.total);
        } else if (response && typeof response === 'object' && 'total' in response) {
          setTotalInteractions(response.total);
        } else if (Array.isArray(response)) {
          setTotalInteractions(response.length);
        }
      } catch (error) {
        console.error('Error loading total interactions count:', error);
      }
    };
    
    loadTotalCount();
  }, []);

  // Load contacts and companies for dropdowns
  useEffect(() => {
    let isCancelled = false;

    const loadOptions = async () => {
      try {
        const [contactsResp, companiesResp] = await Promise.all([
          contactsService.getAll({ page: 1, limit: 1000 }),
          companiesService.getAll({ page: 1, limit: 1000 })
        ]);

        if (!isCancelled) {
          const contacts = (contactsResp?.data || []).map((c: any) => ({
            value: c.id,
            label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || c.id
          }));
          const companies = (companiesResp?.data || []).map((co: any) => ({
            value: co.id,
            label: co.name || co.company_name || co.id
          }));
          setContactOptions(contacts);
          setCompanyOptions(companies);
        }
      } catch (err) {
        console.error('Failed to load contact/company options', err);
      }
    };

    loadOptions();
    return () => { isCancelled = true; };
  }, []);

  const handleViewInteraction = (interaction: any) => {
    setSelectedInteraction(interaction);
    setIsViewModalOpen(true);
  };

  const handleEditInteraction = (interaction: any) => {
    setSelectedInteraction(interaction);
    setIsEditModalOpen(true);
  };

  const handleDeleteInteraction = async (interaction: any) => {
    try {
      await interactionService.delete(interaction.id);
      setTotalInteractions(prev => Math.max(0, prev - 1));
      // If the deleted interaction was being viewed, close the view modal
      if (selectedInteraction && selectedInteraction.id === interaction.id) {
        setIsViewModalOpen(false);
        setSelectedInteraction(null);
      }
    } catch (error) {
      console.error('Failed to delete interaction:', error);
      throw error; // Re-throw to let FlexibleCRUDTable handle the error display
    }
  };

  const handleCreateInteraction = () => {
    setIsCreateModalOpen(true);
  };

  const handleSaveInteraction = async (data: any) => {
    // Fetch updated interaction to refresh the selected interaction data
    const updatedInteraction = await interactionService.update(selectedInteraction.id, data);
    setIsEditModalOpen(false);

    // Update selected interaction with fresh data if view modal is open
    if (isViewModalOpen) {
      setSelectedInteraction(updatedInteraction);
    } else {
      setSelectedInteraction(null);
    }

  };

  const handleCreateNewInteraction = async (interaction: any) => {
    const created = await interactionService.create(interaction);
    setCreatedInteraction(created);
    setTotalInteractions(prev => prev + 1);
    setIsCreateModalOpen(false);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedInteraction(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedInteraction(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="w-full py-6">
        {/* Total Count Display */}
        <div className="px-4 py-4">
          <div className="bg-[var(--color-surface-bg)] overflow-hidden shadow-sm rounded-lg border border-[var(--color-border)]">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-[var(--color-success)] rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-[var(--color-accent-text)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-[var(--color-secondary-text)] truncate">
                      Total Interactions
                    </dt>
                    <dd className="text-lg font-medium text-[var(--color-surface-text)]">
                      {totalInteractions}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 py-6">
          <FlexibleCRUDTable
            entity={interactionEntity}
            service={interactionService}
            onView={handleViewInteraction}
            onEdit={handleEditInteraction}
            onDelete={handleDeleteInteraction}
            onCreate={handleCreateInteraction}
            externalCreatedItem={createdInteraction}
            onOperationComplete={() => {
              // Table will automatically refresh after operations
            }}
            searchPlaceholder={t('crm.interactions.searchPlaceholder')}
            showPagination={true}
            showSearch={true}
            pageSize={10}
          />
        </div>
      </div>

      {/* View Interaction Modal */}
      {isViewModalOpen && selectedInteraction && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseViewModal}
          title={`View ${interactionEntity.displayNameSingular}`}
          data={selectedInteraction}
          fields={interactionEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}
      
      {/* Edit Interaction Modal */}
      {isEditModalOpen && selectedInteraction && (
        <EditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          title={`Edit ${interactionEntity.displayNameSingular}`}
          entity={interactionEntity}
          initialData={selectedInteraction}
          onSubmit={handleSaveInteraction}
          onCancel={handleCloseEditModal}
          loading={false}
          dynamicOptions={{
            contact_id: contactOptions,
            company_id: companyOptions
          }}
        />
      )}
      
      {/* Create Interaction Modal */}
      {isCreateModalOpen && (
        <EditModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          title={`Create ${interactionEntity.displayNameSingular}`}
          entity={interactionEntity}
          onSubmit={handleCreateNewInteraction}
          onCancel={handleCloseCreateModal}
          loading={false}
          dynamicOptions={{
            contact_id: contactOptions,
            company_id: companyOptions
          }}
        />
      )}
    </div>
  );
};
